import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

export interface UserActivityAction {
  type: string;
  timestamp: number;
  meta?: Record<string, unknown>;
}

interface UseUserActivityReturn {
  recentActions: UserActivityAction[];
  trackAction: (type: string, meta?: Record<string, unknown>) => void;
  clearActions: () => void;
}

const MAX_ACTIONS = 20;

export function useUserActivity(): UseUserActivityReturn {
  const location = useLocation();
  const [recentActions, setRecentActions] = useState<UserActivityAction[]>([]);
  const isInitialRender = useRef(true);

  const trackAction = useCallback((type: string, meta?: Record<string, unknown>) => {
    setRecentActions((prev) => {
      const action: UserActivityAction = { type, timestamp: Date.now(), ...(meta != null ? { meta } : {}) };
      const next: UserActivityAction[] = [action, ...prev];
      if (next.length > MAX_ACTIONS) {
        return next.slice(0, MAX_ACTIONS);
      }
      return next;
    });
  }, []);

  const clearActions = useCallback(() => {
    setRecentActions([]);
  }, []);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      trackAction('app_init', { path: location.pathname });
      return;
    }
    trackAction('navigation', { path: location.pathname });
  }, [location.pathname, trackAction]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      trackAction(document.hidden ? 'visibility_hidden' : 'visibility_visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [trackAction]);

  const memoizedActions = useMemo(() => recentActions, [recentActions]);

  return {
    recentActions: memoizedActions,
    trackAction,
    clearActions,
  };
}
